import { useState } from "react";
import { StringParam, useQueryParam, useQueryParams } from "use-query-params";
import { hasPendingCloseId } from "@hooks/useOpenWorkRow";
import { useUserStatus } from "@hooks/useUserStatus";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useTasksListFiltersCache } from "@hooks/view/useTasksListFiltersCache";
import { useScope } from "@state/scopeContext.lib";
import type { TasksLanding } from "./tasksLanding";
import { TASK_ID_PARAM } from "./useOpenTaskUrl";
import { useWorkspaceTasksViewLink } from "./useWorkspaceTasksViewLink";
import { useMonthEndCloseViewParams } from "./views/useMonthEndCloseViewParams";
import {
    hasWorkspaceTasksDrillDownLink,
    WORKSPACE_TASKS_DRILL_DOWN_QUERY_PARAMS,
} from "./workspaceTasksDrillDown";
import { resolveWorkspaceTasksLanding } from "./workspaceTasksLanding";

/**
 * The landing the workspace Tasks page content mounts with, or `null` while
 * the page should show its loader. The first resolved landing is kept, so the
 * content's own cache writes and consumed drill-down links don't change it;
 * the page remounts per workspace to resolve a fresh one.
 */
export function useWorkspaceTasksLanding(): TasksLanding | null {
    const { workspaceId } = useScope();
    const { data: userStatus } = useUserStatus();
    const { organizationId, hasCloseStages, hasCloses, isAwaitingFirstFetch } =
        useMonthEndCloseViewParams();
    const [cachedFilters] = useTasksListFiltersCache();
    const [cachedBaseViewId] = useWorkspaceUserCache<string | null>(
        WORKSPACE_USER_CACHE_KEYS.TASKS_PAGE_BASE_VIEW_ID_CACHE_KEY,
    );
    const [drillDownQueryParams] = useQueryParams(
        WORKSPACE_TASKS_DRILL_DOWN_QUERY_PARAMS,
    );
    const [taskId] = useQueryParam(TASK_ID_PARAM, StringParam);

    const resolvedLanding = resolveWorkspaceTasksLanding({
        user: userStatus?.user,
        isAwaitingFirstFetch,
        hasCachedState:
            cachedFilters !== undefined || cachedBaseViewId !== undefined,
        hasDrillDownLink:
            hasWorkspaceTasksDrillDownLink(drillDownQueryParams) ||
            Boolean(taskId) ||
            (workspaceId ? hasPendingCloseId(workspaceId) : false),
        linkedViewId: drillDownQueryParams.view,
        organizationId,
        hasCloseStages,
        hasCloses,
    });
    const [landing, setLanding] = useState(resolvedLanding);

    if (landing === null && resolvedLanding !== null) {
        setLanding(resolvedLanding);
    }

    const isSavingViewLink = useWorkspaceTasksViewLink({
        landing,
        linkedViewId: drillDownQueryParams.view,
    });

    return isSavingViewLink ? null : landing;
}
